import { Component, Fragment, createRef } from 'react';
import { createPortal } from 'react-dom';

import { notify, cloneDeep } from 'libs/common';
import { ChangeStatus } from 'libs/ui';
import { addPaymentComment } from 'services/payments-service';
import { Comments } from './comments/comments';
import { AddComment } from './comments/add-comment';
import { Container, Option, OptionsPopup, OptionsSeparator } from './with-popup.styled';

const types = {
  rfid: 'rfids',
  csc: 'epms',
  plates: 'vehiclePlates',
};

const getRealType = (type) => {
  return types[type] ? types[type] : type;
};

export const withPopup = (WrappedComponent) => {
  return class WithPopup extends Component {
    constructor(props) {
      super(props);
      this.state = { showOptions: false, showForm: '', actionType: '' };

      this.containerRef = createRef();
      this.popupRef = createRef();
    }

    componentDidUpdate(prevProps, prevState) {
      if (prevState.showOptions !== this.state.showOptions) {
        if (this.state.showOptions) {
          document.addEventListener('mousedown', this.handleOutsideClick, true);
          window.addEventListener('scroll', this.hideOptions, true);
          window.addEventListener('resize', this.hideOptions);
        } else {
          this.removeGlobalListeners();
        }
      }
    }

    componentWillUnmount() {
      this.removeGlobalListeners();
    }

    removeGlobalListeners = () => {
      document.removeEventListener('mousedown', this.handleOutsideClick, true);
      window.removeEventListener('scroll', this.hideOptions, true);
      window.removeEventListener('resize', this.hideOptions);
    };

    /**
     * close popup on click outside of the trigger and the (portaled) popup itself
     */
    handleOutsideClick = (event) => {
      const inTrigger = this.containerRef.current && this.containerRef.current.contains(event.target);
      const inPopup = this.popupRef.current && this.popupRef.current.contains(event.target);
      if (!inTrigger && !inPopup) {
        this.hideOptions();
      }
    };

    hideOptions = () => {
      this.setState({ showOptions: false });
    };

    toggleOptions = () => {
      const show = this.state.showOptions;
      this.setState({ showOptions: !show });
    };

    /**
     * count options that pass isVisible, to estimate the popup's rendered height
     */
    getVisibleCount = (optionsArray) => {
      if (!Array.isArray(optionsArray)) {
        return 0;
      }
      return optionsArray.filter((item) => (item.isVisible ? item.isVisible(this.props.row) : true)).length;
    };

    getOptions = (optionsArray) => {
      if (!Array.isArray(optionsArray)) {
        return null;
      }

      const cnt = optionsArray.length;
      const options = [];

      optionsArray.forEach((item, index) => {
        const last = index === cnt - 1;
        const show = item.isVisible ? item.isVisible(this.props.row) : true;
        if (show) {
          const onClick = (event) => {
            event.stopPropagation();
            event.preventDefault();
            this.optionOnClick(item.func || item.action, item.actionType);
          };
          options.push(
            <Fragment key={index}>
              <Option disabled={this.props.disabled} onClick={onClick}>
                {item.icon && <i className={item.icon} />}
                {item.getTitle ? item.getTitle(this.props.row) : item.text || item.title}
              </Option>
              {!last && <OptionsSeparator />}
            </Fragment>
          );
        }
      });
      return options;
    };

    optionOnClick = (func, type) => {
      if (this.props.disabled) {
        return;
      } else {
        if (func === 'status') {
          // status change form
          this.setState({ showForm: 'status', actionType: type });
        } else if (func === 'add-comment' || func === 'show-comments') {
          // comments
          this.setState({ showForm: func, actionType: type });
        } else {
          // common
          if (func) {
            func(this.props.row);
          }
        }
        this.toggleOptions();
      }
    };

    closeForm = () => {
      this.setState({ showForm: '' });
    };

    /**
     * prepare row
     * @param {*} row
     * @returns
     */
    prepareRow = (row) => {
      return {
        id: row.id,
        parking_id: row.parking_id,
        value: row.mean_id,
        status: row.status,
        type: getRealType(row.type),
      };
    };

    /**
     * add comment
     */
    addComment = (data) => {
      // console.log('🚀 ~ withPopup ~ data:', data);
      const update = this.props.updateEntry;
      addPaymentComment(data)
        .then((comment) => {
          //closeForm();
          const out = cloneDeep(this.props.row);
          out.comment = data.comment;
          out.last_comment = data.comment;
          if (!out.comments) {
            out.comments = [];
          }
          out.comments.splice(0, 0, comment);
          // out.comments.push(comment);
          update(data.id, out);
          //refresh();
        })
        .catch((error) => {
          notify('Добавить комментарий', error.toString());
        });
    };

    render() {
      const { left, options, row } = this.props;
      const { showForm, actionType, showOptions } = this.state;

      // popup is portaled to document.body so it renders above the table instead of
      // being clipped by (and forcing a scrollbar on) the table's scrollable container
      const rect = showOptions && this.containerRef.current ? this.containerRef.current.getBoundingClientRect() : null;
      let popupStyle;
      let flip = false;
      if (rect) {
        const ITEM_HEIGHT = 45;
        const SEPARATOR_HEIGHT = 1;
        const GAP = 10;
        const visibleCount = this.getVisibleCount(options);
        const estimatedHeight = visibleCount > 0 ? visibleCount * ITEM_HEIGHT + (visibleCount - 1) * SEPARATOR_HEIGHT : 0;
        const spaceBelow = window.innerHeight - (rect.top + 35);
        flip = estimatedHeight > 0 && spaceBelow < estimatedHeight + GAP;
        popupStyle = {
          position: 'fixed',
          top: flip ? rect.top - estimatedHeight - GAP : rect.top + 35,
          left: rect.left + (rect.width * (left ?? 50)) / 100,
        };
      }

      return (
        <Container ref={this.containerRef}>
          <WrappedComponent popupDisplayHandler={this.toggleOptions} {...this.props} />
          {rect &&
            createPortal(
              <OptionsPopup ref={this.popupRef} left={left} $flip={flip} style={popupStyle}>
                {this.getOptions(options)}
              </OptionsPopup>,
              document.body
            )}
          {this.props.children}
          {showForm === 'status' && <ChangeStatus updateEntry={this.props.updateEntry} type={actionType} close={this.closeForm} info={row} />}
          {showForm === 'add-comment' && <AddComment addComment={this.addComment} type="addComment" removeFormInfo={this.closeForm} info={this.prepareRow(row)} />}
          {showForm === 'show-comments' && <Comments type="comments" removeFormInfo={this.closeForm} comments={row.comments} />}
        </Container>
      );
    }
  };
};
